// src/pages/WalletPage.js
import React, { useEffect, useState } from 'react';
import { getWalletBalance, getTransactions } from '../services/walletService';
import { Wallet, Loader2, RefreshCw, CreditCard, Landmark, Bitcoin, Globe } from 'lucide-react';
import ChargeRequestModal from '../components/wallet/ChargeRequestModal';
import { convertToPersianNumbers } from '../utils/PersianNumberUtils';
import { useAuthContext } from '../context/AuthContext';

const WalletPage = () => {
  const { openAuthModal, getAuthTokens } = useAuthContext(); // اضافه شد
  const [balance, setBalance] = useState('0.00');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [giftCode, setGiftCode] = useState('');
  const [redeemingGift, setRedeemingGift] = useState(false);
  const [giftMessage, setGiftMessage] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [balanceRes, txRes] = await Promise.all([getWalletBalance(), getTransactions()]);
      setBalance(balanceRes.data.balance);
      setTransactions(txRes.data.results || txRes.data || []);
    } catch (err) {
      console.error('Error loading wallet data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleOpenModal = () => setIsModalOpen(true);

  // تابع فعال‌سازی کارت هدیه با دریافت توکن از AuthContext
  const handleRedeemGiftCode = async () => {
    if (!giftCode.trim()) {
      setGiftMessage('❌ لطفاً کد کارت هدیه را وارد کنید.');
      return;
    }
    setRedeemingGift(true);
    setGiftMessage('');

    try {
      // دریافت توکن از Context - توجه: getAuthTokens مستقیماً رشته توکن را برمی‌گرداند
      const token = getAuthTokens();   // ← اصلاح شده (قبلاً tokens?.access)
      if (!token) {
        setGiftMessage('❌ شما وارد نشده‌اید. لطفاً وارد شوید.');
        setTimeout(() => openAuthModal(), 1500);
        setRedeemingGift(false);
        return;
      }

      const res = await fetch('https://api.farzaneh.app/api/v1/wallet/gift-cards/redeem/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ code: giftCode.trim() }),
      });
      const data = await res.json();
      console.log('Response status:', res.status);
      console.log('Response data:', data);

      if (res.ok) {
        setGiftMessage('✅ کارت هدیه با موفقیت فعال شد.');
        setTimeout(() => {
          fetchData(); // به‌روزرسانی موجودی و تراکنش‌ها
          setGiftCode('');
          setGiftMessage('');
        }, 1500);
      } else if (res.status === 401) {
        setGiftMessage('❌ نشست شما منقضی شده است. لطفاً دوباره وارد شوید.');
        setTimeout(() => openAuthModal(), 1500);
      } else {
        let errorMsg = data.error || data.detail || data.message || 
                       (data.non_field_errors && data.non_field_errors[0]) || 
                       'خطا در فعال‌سازی کارت هدیه';
        const alreadyUsedKeywords = [
          'قبلاً استفاده شده', 'already used', 'استفاده شده', 'قبلا استفاده',
          'used before', 'already redeemed'
        ];
        const isAlreadyUsed = alreadyUsedKeywords.some(keyword => 
          errorMsg.toLowerCase().includes(keyword.toLowerCase())
        );
        if (isAlreadyUsed) {
          setGiftMessage('❌ قبلاً از این کد استفاده شده است.');
        } else {
          setGiftMessage(`❌ ${errorMsg}`);
        }
      }
    } catch (err) {
      console.error(err);
      setGiftMessage('❌ خطا در ارتباط با سرور');
    } finally {
      setRedeemingGift(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-10">
        <Loader2 className="animate-spin text-emerald-700" size={32} />
      </div>
    );
  }

  const formattedBalance = convertToPersianNumbers(Number(balance).toLocaleString());
  const newColor = 'oklch(38% 0.189 293.745)';

  return (
    <div className="p-4 max-w-lg mx-auto rtl">
      {/* هدر */}
      <div className="flex items-center gap-2 mb-6">
        <Wallet size={20} className="text-gray-700" />
        <h1 className="text-sm font-extrabold text-gray-700">کیف پول</h1>
      </div>

      {/* کادر موجودی */}
      <div className="border border-violet-700 rounded-xl p-4 mb-6 bg-white">
        <p className="text-violet-700 text-sm font-normal">موجودی فعلی</p>
        <p className="text-violet-700 text-sm font-bold mt-1">
          {formattedBalance} تومان
        </p>
      </div>

      {/* چهار دکمه روش‌های پرداخت */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <button
          onClick={() => alert('درگاه پرداخت آنلاین به زودی اضافه می‌شود.')}
          className="flex flex-col items-center gap-2 bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition"
        >
          <CreditCard size={28} className="text-violet-800" />
          <span className="text-[11px] font-light text-gray-700">پرداخت آنلاین</span>
        </button>
        <button
          onClick={handleOpenModal}
          className="flex flex-col items-center gap-2 bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition"
        >
          <Landmark size={28} className="text-violet-700" />
          <span className="text-[11px] font-light text-gray-700">کارت به کارت</span>
        </button>
        <button
          onClick={handleOpenModal}
          className="flex flex-col items-center gap-2 bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition"
        >
          <Bitcoin size={28} className="text-violet-700" />
          <span className="text-[11px] font-light text-gray-700">رمز ارز</span>
        </button>
        <button
          onClick={handleOpenModal}
          className="flex flex-col items-center gap-2 bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition"
        >
          <Globe size={28} className="text-violet-700" />
          <span className="text-[11px] font-light text-gray-700">سایر روش‌ها</span>
        </button>
      </div>

      {/* بخش فعال‌سازی کارت هدیه */}
      <div className="mb-8 bg-gray-50 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-gray-800 mb-3">فعال‌سازی کارت هدیه</h3>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="کد کارت هدیه را وارد کنید"
            className="flex-1 border border-gray-300 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2"
            style={{ '--tw-ring-color': newColor }}
            value={giftCode}
            onChange={(e) => setGiftCode(e.target.value)}
          />
          <button
            onClick={handleRedeemGiftCode}
            disabled={redeemingGift}
            className="text-white px-4 py-2 rounded-xl text-sm font-medium hover:opacity-90 disabled:opacity-50 transition"
            style={{ backgroundColor: newColor }}
          >
            {redeemingGift ? 'در حال فعال‌سازی...' : 'فعال‌سازی'}
          </button>
        </div>
        {giftMessage && (
          <p className={`text-xs mt-2 ${giftMessage.includes('✅') ? 'text-green-600' : 'text-red-500'}`}>
            {giftMessage}
          </p>
        )}
      </div>

      {/* تراکنش‌های اخیر */}
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-xs font-semibold text-gray-700">تراکنش‌های اخیر</h2>
        <button onClick={fetchData} className="text-emerald-700 hover:text-emerald-900">
          <RefreshCw size={16} />
        </button>
      </div>

      {transactions.length === 0 ? (
        <p className="text-gray-500 text-sm">هنوز تراکنشی ثبت نشده است.</p>
      ) : (
        <div className="space-y-0">
          {transactions.map((tx) => (
            <div key={tx.id} className="flex justify-between items-center py-3 border-b border-gray-200 last:border-b-0">
              <div>
                <p className="text-[11px] font-light text-gray-700">{tx.description || tx.type_display}</p>
                <p className="text-[10px] text-gray-400 mt-1">
                  {new Date(tx.created_at).toLocaleDateString('fa-IR')}
                </p>
              </div>
              <span className={`text-sm font-medium ${tx.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {tx.amount > 0 ? '+' : ''}
                {convertToPersianNumbers(Number(tx.amount).toLocaleString())}
              </span>
            </div>
          ))}
        </div>
      )}

      <ChargeRequestModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    <div className="flex justify-center mt-12 mb-6">
            <a 
              referrerPolicy='origin' 
              target='_blank' 
              href='https://trustseal.enamad.ir/?id=743356&Code=cegf5V01ZJJPrIgoeIrjGinSGGNKk3tQ'
            >
              <img 
                referrerPolicy='origin' 
                src='https://trustseal.enamad.ir/logo.aspx?id=743356&Code=cegf5V01ZJJPrIgoeIrjGinSGGNKk3tQ' 
                alt='نماد اعتماد الکترونیکی' 
                style={{ cursor: 'pointer', width: '100px', height: 'auto' }} 
                code='cegf5V01ZJJPrIgoeIrjGinSGGNKk3tQ'
              />
            </a>
      </div>        
    </div>
    
  );
};

export default WalletPage;